import React, { memo, useState } from 'react';
import { View } from 'react-native';
import { createUseStyle } from '@redlink/assets/styles';
import {
  Space,
  Card,
  LayoutGrid,
  IconLink,
  IconStore,
  IconGallery,
  LoadingContainer,
} from '@redlink/components/atoms';
import { Button, usePlan } from '@redlink/components/molecules';
import { useLocale } from '@redlink/assets/locale';
import { useMoneyMask } from '@redlink/core';
import { BodyBlockContainer } from './bodyBlockContainer';
import { ReportSummaryList } from './reportSummaryList';
import { ReportTopList } from './reportTopList';
import { useQuery, getPageLinkStatsQuery } from '@redlink/services';

export const LinkBlock = memo(({ isProUser }) => {
  const { dictionary } = useLocale();
  const { getValue } = useMoneyMask();
  const [periodType, setPeriodType] = useState(
    isProUser ? 'lastMonth' : 'last7Days'
  );
  const [selectedLinkType, setSelectedLinkType] = useState('external');
  const { showPlanDialog } = usePlan();

  const {
    loading,
    data: {
      PageLinkStats: {
        clickCount = 0,
        internalClickCount = 0,
        externalClickCount = 0,
        topItems = [],
      } = {},
    },
  } = useQuery(getPageLinkStatsQuery, {
    variables: {
      period: periodType,
      type: selectedLinkType,
    },
    limited: () => {
      showPlanDialog();
    },
  });

  const data = [
    {
      title: dictionary.internalLinkClick,
      value: internalClickCount,
      valueFormatter: (value) => getValue(value),
    },
    {
      title: dictionary.externalLinkClick,
      value: externalClickCount,
      valueFormatter: (value) => getValue(value),
    },
    {
      title: dictionary.allLinkClick,
      value: clickCount,
      valueFormatter: (value) => getValue(value),
    },
  ];

  return (
    <Card>
      <LoadingContainer isBusy={loading}>
        <LayoutGrid>
          <View>
            <LinkTypeSwitch
              {...{ selectedLinkType }}
              onChange={(type) => setSelectedLinkType(type)}
            />
            <BodyBlockContainer
              isProUser={isProUser}
              selectedPeriodTypeKey={periodType}
              onChangePeriodTypeKey={(i) => setPeriodType(i)}
            >
              <ReportSummaryList {...{ data }} />
            </BodyBlockContainer>
          </View>
          <ReportTopList data={topItems} />
        </LayoutGrid>
      </LoadingContainer>
    </Card>
  );
});

const LinkTypeSwitch = memo(({ selectedLinkType, onChange }) => {
  const { styles, colors } = useStyle();
  const { dictionary } = useLocale();
  const isLinksSelected = selectedLinkType === 'external';
  const isGallerySelected = selectedLinkType === 'post';
  const isShopSelected = selectedLinkType === 'product';
  return (
    <View style={styles.titleButtonsContainer}>
      <Button
        style={styles.titleButton}
        title={dictionary.links}
        variant={isLinksSelected ? 'switchSelected' : 'switch'}
        icon={
          <IconLink color={isLinksSelected ? colors.white : colors.gray23} />
        }
        onPress={() => onChange('external')}
      />
      <Space size={8} />
      <Button
        style={styles.titleButton}
        title={dictionary.gallery}
        variant={isGallerySelected ? 'switchSelected' : 'switch'}
        icon={
          <IconGallery
            color={isGallerySelected ? colors.white : colors.gray23}
          />
        }
        onPress={() => onChange('post')}
      />
      <Space size={8} />
      <Button
        style={styles.titleButton}
        title={dictionary.shop}
        variant={isShopSelected ? 'switchSelected' : 'switch'}
        icon={
          <IconStore color={isShopSelected ? colors.white : colors.gray23} />
        }
        onPress={() => onChange('product')}
      />
    </View>
  );
});

const useStyle = createUseStyle(({ colors }) => ({
  titleText: {
    color: colors.black,
    fontSize: 14,
  },
  itemTextButton: {
    alignSelf: 'flex-start',
    fontSize: 16,
    paddingHorizontal: 0,
  },
  titleButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  titleButton: {
    flex: 1,
  },
}));
